import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ActivityIndicator } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaTwitter, FaTelegram, FaTiktok, FaInstagram, FaGlobe, FaNewspaper, FaFacebookF, FaTv,
  FaBinoculars, FaSync, FaPause, FaPlay, FaArchive,
  FaChevronDown, FaArrowUp, FaArrowDown, FaBolt, FaFlag,
  FaChartLine, FaShieldAlt, FaBookmark, FaCog, FaEye,
  FaTimes, FaCheck, FaExclamationTriangle, FaPen, FaSearch,
  FaExternalLinkAlt, FaFire,
} from 'react-icons/fa';
import ListsManager from '../services/ListsManager';
import AnalyticsService from '../services/AnalyticsService';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';

// ── constants ──────────────────────────────────────────────────────────────

let T = {};

const TRACKER_TYPE_COLORS = {
  action_outcome:      '#0a84ff',
  content_report:      '#ff453a',
  narrative_evolution:  '#bf5af2',
  saved_content:       '#30d158',
  custom:              '#8e8e93',
};

const getTrackerTypeLabels = (t) => ({
  action_outcome:      t('app.tracking.typeActionOutcome'),
  content_report:      t('app.tracking.typeContentReport'),
  narrative_evolution:  t('app.tracking.typeNarrativeEvolution'),
  saved_content:       t('app.tracking.typeSavedContent'),
  custom:              t('app.tracking.typeCustom'),
});

const TRACKER_TYPE_ICONS = {
  action_outcome:      FaShieldAlt,
  content_report:      FaFlag,
  narrative_evolution:  FaChartLine,
  saved_content:       FaBookmark,
  custom:              FaEye,
};

const STATUS_CONFIG = {
  active:    { label: 'Active',    color: '#30d158', bg: 'rgba(48,209,88,0.12)' },
  paused:    { label: 'Paused',    color: '#ffd60a', bg: 'rgba(255,214,10,0.12)' },
  completed: { label: 'Completed', color: '#0a84ff', bg: 'rgba(10,132,255,0.12)' },
  archived:  { label: 'Archived',  color: '#8e8e93', bg: 'rgba(142,142,147,0.12)' },
};

const PLATFORM_ICONS = {
  twitter:        FaTwitter,
  telegram:       FaTelegram,
  tiktok:         FaTiktok,
  instagram:      FaInstagram,
  facebook_group: FaFacebookF,
  rss:            FaGlobe,
  webz:           FaNewspaper,
  tv:             FaTv,
};

const PLATFORM_COLORS = {
  twitter:        '#1da1f2',
  telegram:       '#0088cc',
  tiktok:         '#ff0050',
  instagram:      '#e1306c',
  facebook_group: '#1877F2',
  rss:            '#22c55e',
  webz:           '#10b981',
  tv:             '#9f1239',
};

const EVENT_ICONS = {
  content_removed:           FaFlag,
  content_restored:          FaCheck,
  content_changed:           FaPen,
  sentiment_shift:           FaChartLine,
  velocity_spike:            FaBolt,
  engagement_milestone:      FaArrowUp,
  narrative_status_changed:  FaExclamationTriangle,
};

const EVENT_COLORS = {
  content_removed:           '#ff453a',
  content_restored:          '#30d158',
  content_changed:           '#ff9f0a',
  sentiment_shift:           '#0a84ff',
  velocity_spike:            '#ff9f0a',
  engagement_milestone:      '#30d158',
  narrative_status_changed:  '#bf5af2',
};

function _timeAgo(iso, t) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('app.tracking.justNow');
  if (mins < 60) return t('app.tracking.minutesAgo', { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('app.tracking.hoursAgo', { n: hrs });
  return t('app.tracking.daysAgo', { n: Math.floor(hrs / 24) });
}

function fmtNum(n) {
  if (n == null) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K`;
  if (typeof n === 'number' && !Number.isInteger(n)) return n.toFixed(2);
  return `${n}`;
}

function fmtDeltaPct(n) {
  if (n == null) return '';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(0)}%`;
}

function fmtPct(n) {
  if (n == null) return '';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${(n * 100).toFixed(0)}%`;
}

function sigColor(s) {
  if (s >= 0.7) return '#ff453a';
  if (s >= 0.4) return '#ff9f0a';
  if (s >= 0.2) return '#ffd60a';
  return '#8e8e93';
}

function sigLabel(s, t) {
  if (s >= 0.7) return t('app.tracking.severity.critical');
  if (s >= 0.4) return t('app.tracking.severity.high');
  if (s >= 0.2) return t('app.tracking.severity.medium');
  return t('app.tracking.severity.stable');
}

function extractTikTokId(url) {
  if (!url) return null;
  const m = url.match(/\/video\/(\d+)/);
  return m ? m[1] : null;
}

function extractTweetId(url) {
  if (!url) return null;
  const m = url.match(/\/status\/(\d+)/);
  return m ? m[1] : null;
}

// ── Sparkline component ──────────────────────────────────────────────────

const SPARK_W = 300;
const SPARK_H = 36;
const SPARK_PAD = 4;

function MiniSparkline({ values, color, label, current }) {
  const { t } = useTranslation();
  if (!values || values.length < 2) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
        <span style={{ color: T.textMuted, fontSize: 11, width: 80 }}>{label}</span>
        <span style={{ color: T.text, fontSize: 13, fontWeight: 600 }}>{current != null ? fmtNum(current) : '—'}</span>
      </div>
    );
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max === min ? 1 : max - min;
  const toX = (i) => (i / (values.length - 1)) * SPARK_W;
  const toY = (v) => SPARK_PAD + (SPARK_H - 2 * SPARK_PAD) - ((v - min) / range) * (SPARK_H - 2 * SPARK_PAD);
  const points = values.map((v, i) => ({ x: toX(i), y: toY(v) }));
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const areaD = pathD + ` L ${points[points.length - 1].x.toFixed(1)} ${SPARK_H} L 0 ${SPARK_H} Z`;
  const first = values[0];
  const last = values[values.length - 1];
  const change = first > 0 ? ((last - first) / first) : 0;
  const changeColor = change >= 0 ? '#30d158' : '#ff453a';
  const uid = `spark-${label.replace(/\s/g, '')}-${Date.now()}`;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
      <div style={{ width: 80 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 6, height: 6, borderRadius: 3, background: color }} />
          <span style={{ color: T.textSec, fontSize: 11, fontWeight: 500 }}>{label}</span>
        </div>
        <span style={{ color: changeColor, fontSize: 10, marginLeft: 10 }}>{fmtPct(change)}</span>
      </div>
      <svg width="100%" height={SPARK_H} viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} preserveAspectRatio="none" style={{ flex: 1, maxWidth: 200 }}>
        <defs>
          <linearGradient id={uid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={areaD} fill={`url(#${uid})`} />
        <path d={pathD} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="3" fill={color} />
      </svg>
      <div style={{ width: 56, textAlign: 'right' }}>
        <div style={{ color: T.text, fontSize: 13, fontWeight: 700 }}>{fmtNum(last)}</div>
        <div style={{ color: T.textMuted, fontSize: 10 }}>{t('app.tracking.now')}</div>
      </div>
    </div>
  );
}

// ── TrackingScreen ──────────────────────────────────────────────────────

export default function TrackingScreen() {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { activeOrg, orgs, mode } = useOrg();
  const navigate = useNavigate();
  const { trackerId: urlTrackerId } = useParams();

  const TRACKER_TYPE_LABELS = getTrackerTypeLabels(t);

  T = {
    bg:        theme.bg,
    card:      theme.bgCard,
    surface:   theme.bgSurface,
    border:    theme.border,
    text:      theme.textPrimary,
    textSec:   theme.textSecondary,
    textMuted: theme.textMuted,
    primary:   theme.accentBlue   || '#0a84ff',
    purple:    theme.accentPurple || '#bf5af2',
    warning:   theme.accentYellow || '#ffd60a',
    success:   theme.accentGreen  || '#30d158',
    error:     theme.accentRed    || '#ff453a',
    elevated:  theme.bgElevated,
  };

  const [trackers, setTrackers]         = useState(null);
  const [summary, setSummary]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [statusTab, setStatusTab]       = useState('active');
  const [typeFilter, setTypeFilter]     = useState(null);
  const [selectedId, setSelectedId]     = useState(urlTrackerId || null);
  const [detail, setDetail]             = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshing, setRefreshing]     = useState(false);
  const [enriching, setEnriching]       = useState(false);
  const [actionDropdown, setActionDropdown] = useState(false);
  const [filtersOpen, setFiltersOpen]     = useState(false);
  const [sidebarW, setSidebarW]         = useState(300);
  const [rightW, setRightW]             = useState(420);
  const dragRef     = useRef(null);
  const dragStartRef = useRef(null);
  const autoEnrichedRef = useRef(new Set());

  const startDrag = (divider, e) => {
    e.preventDefault();
    dragRef.current = divider;
    dragStartRef.current = { x: e.clientX, sidebarW, rightW };
    const onMove = (mv) => {
      const rawDx = mv.clientX - dragStartRef.current.x;
      const dx = i18n.language === 'he' ? -rawDx : rawDx;
      if (dragRef.current === 'left') {
        setSidebarW(Math.max(220, Math.min(420, dragStartRef.current.sidebarW + dx)));
      } else {
        setRightW(Math.max(260, Math.min(480, dragStartRef.current.rightW - dx)));
      }
    };
    const onUp = () => {
      dragRef.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Data fetching ────────────────────────────────────────────────────

  const fetchList = useCallback(async () => {
    const orgList = mode === 'all' && orgs.length > 0 ? orgs : activeOrg ? [activeOrg] : [];
    if (orgList.length === 0) return;

    if (mode === 'all' && orgs.length > 0) {
      const perOrg = await Promise.all(
        orgs.map(o => ListsManager.fetchTrackers(o.id, statusTab, typeFilter).then(r => r?.trackers || []))
      );
      const seen = new Set();
      const merged = perOrg.flat().filter(t => {
        if (seen.has(t.id)) return false;
        seen.add(t.id);
        return true;
      });
      setTrackers(merged);

      const summaries = await Promise.all(orgs.map(o => ListsManager.fetchTrackerSummary(o.id)));
      const mergedSummary = { by_type: {}, by_status: {}, total: 0 };
      for (const s of summaries) {
        if (!s) continue;
        for (const [k, v] of Object.entries(s.by_type || {})) mergedSummary.by_type[k] = (mergedSummary.by_type[k] || 0) + v;
        for (const [k, v] of Object.entries(s.by_status || {})) mergedSummary.by_status[k] = (mergedSummary.by_status[k] || 0) + v;
        mergedSummary.total += s.total || 0;
      }
      setSummary(mergedSummary);
    } else {
      const result = await ListsManager.fetchTrackers(activeOrg.id, statusTab, typeFilter);
      if (result) setTrackers(result.trackers);
      const sum = await ListsManager.fetchTrackerSummary(activeOrg.id);
      if (sum) setSummary(sum);
    }
    setLoading(false);
  }, [activeOrg?.id, mode, orgs, statusTab, typeFilter]);

  useEffect(() => { fetchList(); AnalyticsService.logActivity('view_tracking'); }, [fetchList]);

  useEffect(() => {
    if (urlTrackerId) setSelectedId(urlTrackerId);
  }, [urlTrackerId]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setDetailLoading(true);
    ListsManager.fetchTrackerDetail(selectedId).then(d => {
      setDetail(d);
      setDetailLoading(false);
    });
  }, [selectedId]);

  useEffect(() => {
    if (!trackers || selectedId) return;
    if (trackers.length > 0) setSelectedId(trackers[0].id);
  }, [trackers, selectedId]);

  // ── Auto-enrich on detail load ──────────────────────────────────────
  useEffect(() => {
    if (!detail || !selectedId || enriching) return;
    const snap = detail.snapshots && detail.snapshots.length > 0 ? detail.snapshots[detail.snapshots.length - 1] : null;
    if (!snap || snap.metrics?.enrichment || autoEnrichedRef.current.has(selectedId)) return;

    autoEnrichedRef.current.add(selectedId);
    setEnriching(true);
    ListsManager.enrichTracker(selectedId).then(result => {
      if (result && !result.error) {
        ListsManager.fetchTrackerDetail(selectedId).then(d => { if (d) setDetail(d); });
      }
      setEnriching(false);
    });
  }, [detail, selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Actions ──────────────────────────────────────────────────────────

  const handleRefresh = async () => {
    if (!selectedId || refreshing) return;
    setRefreshing(true);
    const result = await ListsManager.refreshTracker(selectedId);
    if (result && !result.error) {
      const d = await ListsManager.fetchTrackerDetail(selectedId);
      if (d) setDetail(d);
      await fetchList();
    }
    setRefreshing(false);
  };

  const handleEnrich = async () => {
    if (!selectedId || enriching) return;
    setEnriching(true);
    const result = await ListsManager.enrichTracker(selectedId);
    if (result && !result.error) {
      const d = await ListsManager.fetchTrackerDetail(selectedId);
      if (d) setDetail(d);
    }
    setEnriching(false);
  };

  const handleUpdate = async (body) => {
    if (!selectedId) return;
    const updated = await ListsManager.updateTracker(selectedId, body);
    if (updated) {
      const d = await ListsManager.fetchTrackerDetail(selectedId);
      if (d) setDetail(d);
      await fetchList();
    }
    setActionDropdown(false);
  };

  useEffect(() => {
    if (!actionDropdown) return;
    const close = () => setActionDropdown(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [actionDropdown]);

  // ── Render helpers ───────────────────────────────────────────────────

  const sel = detail?.tracker;
  const snaps = detail?.snapshots || [];
  const events = detail?.events || [];
  const baseline = snaps.find(s => s.is_baseline);
  const latest = snaps.length > 0 ? snaps[snaps.length - 1] : null;

  // ── Empty state ──────────────────────────────────────────────────────

  if (!activeOrg && !(mode === 'all' && orgs.length > 0)) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: T.textMuted }}>
        <span>{t('app.tracking.selectOrg')}</span>
      </div>
    );
  }

  // ── Main layout ──────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', height: '100%', background: T.bg, color: T.text, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <style>{`
        .trk-card { transition: all .2s cubic-bezier(0.4, 0, 0.2, 1); }
        .trk-card:hover { background: ${T.surface} !important; transform: translateX(2px); }
        .trk-btn { transition: all .15s; }
        .trk-btn:hover { opacity: 0.88; transform: translateY(-1px); }
        .trk-btn:active { transform: scale(0.97); }
        .trk-tab { padding: 5px 14px; border-radius: 20px; font-size: 12px; font-weight: 600; cursor: pointer; border: none; transition: all .15s; }
        .trk-chip { padding: 4px 10px; border-radius: 20px; font-size: 10px; font-weight: 600; cursor: pointer; border: 1px solid ${T.border}; background: ${T.surface}; color: ${T.textMuted}; transition: all .15s; }
        .trk-chip:hover { border-color: ${T.primary}66; }
        .trk-chip-active { background: ${T.primary}22; border-color: ${T.primary}; color: ${T.primary}; }
        .trk-divider { width: 5px; cursor: col-resize; background: transparent; transition: background .15s; flex-shrink: 0; }
        .trk-divider:hover { background: ${T.primary}33; }
        .trk-dropdown { position: absolute; inset-inline-end: 0; bottom: 40px; background: ${T.elevated || T.card}; border: 1px solid ${T.border}; border-radius: 12px; box-shadow: 0 12px 32px rgba(0,0,0,0.3); z-index: 100; min-width: 180px; overflow: hidden; backdrop-filter: blur(20px); }
        .trk-dropdown-item { padding: 11px 16px; font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 10px; transition: background .12s; }
        .trk-dropdown-item:hover { background: ${T.surface}; }
        .trk-timeline-dot { width: 14px; height: 14px; border-radius: 7px; flex-shrink: 0; border: 3px solid transparent; }
        .trk-timeline-line { width: 3px; background: linear-gradient(180deg, ${T.border}, ${T.border}44); flex-shrink: 0; border-radius: 2px; }
        .trk-section { background: ${T.card}; border: 1px solid ${T.border}; border-radius: 14px; padding: 24px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03); }
        .trk-section-hdr { display: flex; align-items: center; gap: 10px; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 1px solid ${T.border}33; position: relative; }
        .trk-metric-card { transition: all .2s; }
        .trk-metric-card:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.12); }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 3px; }
      `}</style>

      {/* ── Left Panel: Tracker List ── */}
      <div style={{ width: sidebarW, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${T.border}`, background: T.bg }}>
        {/* Header */}
        <div style={{ padding: '16px 16px 14px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ width: 32, height: 32, borderRadius: 10, background: `${T.primary}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaBinoculars style={{ color: T.primary, fontSize: 15 }} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.2 }}>{t('app.tracking.title')}</div>
              {summary && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>{summary.total} {t('app.tracking.total')}</div>}
            </div>
          </div>

          {/* Status tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
            {['active', 'completed'].map(s => {
              const cfg = STATUS_CONFIG[s];
              const isActive = statusTab === s;
              return (
                <button key={s} className="trk-tab" onClick={() => { setStatusTab(s); setSelectedId(null); }}
                  style={{
                    background: isActive ? `${cfg.color}22` : 'transparent',
                    color: isActive ? cfg.color : T.textMuted,
                    border: `1px solid ${isActive ? cfg.color + '44' : 'transparent'}`,
                  }}>
                  {t(`app.tracking.status.${s}`)}
                  {summary?.by_status?.[s] != null && <span style={{ marginLeft: 4, opacity: 0.7 }}>({summary.by_status[s]})</span>}
                </button>
              );
            })}
          </div>

          {/* Filter toggle */}
          <div
            onClick={() => setFiltersOpen(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '6px 0' }}
          >
            <FaChevronDown style={{ fontSize: 9, color: T.textMuted, transform: filtersOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted }}>Filters</span>
            {typeFilter && <span style={{ width: 6, height: 6, borderRadius: 3, background: T.primary }} />}
          </div>

          {filtersOpen && (<>
          {summary && summary.by_type && Object.keys(summary.by_type).length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12, marginTop: 8 }}>
              {Object.entries(summary.by_type).map(([type, count]) => {
                const TIcon = TRACKER_TYPE_ICONS[type] || FaEye;
                return (
                  <div key={type} style={{
                    display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 12,
                    background: `${TRACKER_TYPE_COLORS[type] || '#8e8e93'}12`,
                    border: `1px solid ${TRACKER_TYPE_COLORS[type] || '#8e8e93'}28`,
                  }}>
                    <TIcon style={{ fontSize: 9, color: TRACKER_TYPE_COLORS[type] || '#8e8e93' }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: TRACKER_TYPE_COLORS[type] || '#8e8e93' }}>{count}</span>
                    <span style={{ fontSize: 10, color: T.textMuted }}>{TRACKER_TYPE_LABELS[type] || type}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Type filter chips */}
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            <span className={`trk-chip ${!typeFilter ? 'trk-chip-active' : ''}`}
              onClick={() => { setTypeFilter(null); setSelectedId(null); }}>{t('app.tracking.all')}</span>
            {Object.entries(TRACKER_TYPE_LABELS).map(([key, label]) => (
              <span key={key} className={`trk-chip ${typeFilter === key ? 'trk-chip-active' : ''}`}
                onClick={() => { setTypeFilter(typeFilter === key ? null : key); setSelectedId(null); }}>{label}</span>
            ))}
          </div>
          </>)}
        </div>

        {/* Tracker list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 60 }}>
              <ActivityIndicator size="small" color={T.primary} />
              <span style={{ fontSize: 12, color: T.textMuted }}>{t('app.tracking.loading')}</span>
            </div>
          ) : !trackers || trackers.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '48px 24px', textAlign: 'center' }}>
              <FaBinoculars style={{ fontSize: 36, color: T.textMuted, opacity: 0.15 }} />
              <div style={{ color: T.textSec, fontSize: 14, fontWeight: 500 }}>{t('app.tracking.noTrackers')}</div>
              <div style={{ color: T.textMuted, fontSize: 12, lineHeight: 1.5 }}>{t('app.tracking.noTrackersHint')}</div>
            </div>
          ) : trackers.map(tr => {
            const TypeIcon = TRACKER_TYPE_ICONS[tr.tracker_type] || FaEye;
            const PlatIcon = PLATFORM_ICONS[tr.source_platform];
            const typeColor = TRACKER_TYPE_COLORS[tr.tracker_type] || '#8e8e93';
            const isSelected = selectedId === tr.id;

            return (
              <div key={tr.id} className="trk-card"
                onClick={() => { setSelectedId(tr.id); navigate(`/tracking/${tr.id}`, { replace: true }); }}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 16px', cursor: 'pointer',
                  minHeight: 80,
                  background: isSelected ? `${T.primary}15` : 'transparent',
                  borderLeft: `4px solid ${isSelected ? T.primary : typeColor}`,
                  borderBottom: `1px solid ${T.border}`,
                  boxShadow: isSelected ? `inset 0 0 0 1px ${T.primary}22` : 'none',
                }}>
                {/* Row 1: Title + significance */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
                      <TypeIcon style={{ fontSize: 12, color: typeColor, flexShrink: 0 }} />
                      <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {tr.title}
                      </span>
                    </div>
                    {/* Row 2: Badges */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, color: typeColor, fontWeight: 600,
                        padding: '2px 7px', borderRadius: 8, background: `${typeColor}15` }}>
                        {TRACKER_TYPE_LABELS[tr.tracker_type] || tr.tracker_type}
                      </span>
                      {PlatIcon && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: PLATFORM_COLORS[tr.source_platform] || T.textMuted }}>
                          <PlatIcon style={{ fontSize: 10 }} />
                        </span>
                      )}
                      {tr.auto_refresh && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 9, color: T.success, opacity: 0.7 }}>
                          <FaSync style={{ fontSize: 8 }} /> {t('app.tracking.auto')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {/* Row 3: Summary line */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: T.textMuted, paddingLeft: 19 }}>
                  {tr.snapshot_count != null && <span>{tr.snapshot_count} snapshots</span>}
                  {tr.event_count != null && tr.event_count > 0 && (
                    <span style={{ color: T.warning }}>
                      <FaBolt style={{ fontSize: 8, marginRight: 2 }} />{tr.event_count} events
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 10 }}>
                    {_timeAgo(tr.updated_at || tr.created_at, t)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Draggable divider (left) ── */}
      <div className="trk-divider" onMouseDown={(e) => startDrag('left', e)} />

      {/* ── CENTER + RIGHT area ── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {detailLoading ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 14 }}>
            <ActivityIndicator size="large" color={T.primary} />
            <span style={{ fontSize: 13, color: T.textMuted }}>{t('app.tracking.loadingDetail')}</span>
          </div>
        ) : !sel ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, color: T.textMuted }}>
            <div style={{ width: 72, height: 72, borderRadius: 20, background: `${T.primary}10`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaBinoculars style={{ fontSize: 28, opacity: 0.3 }} />
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: T.textSec, marginBottom: 4 }}>{t('app.tracking.selectTracker')}</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>{t('app.tracking.selectTrackerHint')}</div>
            </div>
          </div>
        ) : (
          <>
            {/* ── CENTER column: main content ── */}
            <div style={{ flex: 1, overflowY: 'auto', background: T.bg }}>
              <div style={{ padding: '20px 24px' }}>
                {/* ── Change 1: Crisis Summary Banner (ABOVE hero) ── */}
                {sel.highest_significance >= 0.4 && latest?.delta?.since_baseline && (
                  <div style={{
                    background: sel.highest_significance >= 0.7
                      ? 'linear-gradient(135deg, #ff453a, #ff6961)'
                      : 'linear-gradient(135deg, #ff9f0a, #ffb340)',
                    color: '#fff',
                    padding: '16px 20px',
                    borderRadius: 12,
                    marginBottom: 16,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    border: '1px solid rgba(255,255,255,0.2)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <FaExclamationTriangle style={{ fontSize: 20 }} />
                      <span style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        {sel.highest_significance >= 0.7 ? t('app.tracking.severity.critical') : t('app.tracking.severity.needsAttention')}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.5, opacity: 0.95 }}>
                      {(() => {
                        const sb = latest.delta.since_baseline;
                        const biggestChange = Object.entries(sb)
                          .filter(([k, v]) => typeof v === 'object' && typeof v.pct_change === 'number' && Math.abs(v.pct_change) > 10)
                          .sort((a, b) => Math.abs(b[1].pct_change) - Math.abs(a[1].pct_change))[0];

                        if (biggestChange) {
                          const [key, val] = biggestChange;
                          const metricName = t(`app.tracking.metricKeys.${key}`, { defaultValue: key });
                          const pct = val.pct_change >= 0 ? `+${val.pct_change.toFixed(0)}%` : `${val.pct_change.toFixed(0)}%`;
                          return `${metricName} ${val.pct_change >= 0 ? 'jumped' : 'dropped'} ${pct} since you started tracking. ${val.pct_change >= 100 ? 'Content is spreading rapidly.' : val.pct_change <= -50 ? 'Significant decline detected.' : 'Notable change in engagement.'}`;
                        }
                        return 'Significant changes detected in tracked metrics.';
                      })()}
                    </div>
                  </div>
                )}

                {/* ── Hero Header (with severity labels instead of decimal) ── */}
                <div style={{
                  background: `linear-gradient(135deg, ${T.elevated || T.card}, ${T.surface})`,
                  border: `1px solid ${T.border}`,
                  borderRadius: 16,
                  padding: '24px 28px',
                  marginBottom: 20,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  overflow: 'visible',
                }}>
                  {/* Title */}
                  <div style={{ marginBottom: 12 }}>
                    <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, lineHeight: 1.3 }}>{sel.title}</h2>
                    {sel.description && <p style={{ margin: '6px 0 0', fontSize: 13, color: T.textSec, lineHeight: 1.5 }}>{sel.description}</p>}
                  </div>

                  {/* Badges row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                    {(() => {
                      const tc = TRACKER_TYPE_COLORS[sel.tracker_type] || '#8e8e93';
                      const TI = TRACKER_TYPE_ICONS[sel.tracker_type] || FaEye;
                      return (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px',
                          borderRadius: 12, fontSize: 12, fontWeight: 600, color: tc, background: `${tc}15` }}>
                          <TI style={{ fontSize: 11 }} /> {TRACKER_TYPE_LABELS[sel.tracker_type] || sel.tracker_type}
                        </span>
                      );
                    })()}
                    {sel.source_platform && (() => {
                      const PI = PLATFORM_ICONS[sel.source_platform];
                      const pc = PLATFORM_COLORS[sel.source_platform] || T.textMuted;
                      return PI ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px',
                          borderRadius: 12, fontSize: 12, fontWeight: 500, color: pc, background: `${pc}12` }}>
                          <PI style={{ fontSize: 11 }} /> {sel.source_platform}
                        </span>
                      ) : null;
                    })()}
                    <span style={{ fontSize: 11, color: T.textMuted }}>{t('app.tracking.created')} {_timeAgo(sel.created_at, t)}</span>
                  </div>

                  {/* Action toolbar */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingTop: 14, borderTop: `1px solid ${T.border}44`, flexWrap: 'wrap' }}>
                    <button className="trk-btn" onClick={() => handleUpdate({ auto_refresh: !sel.auto_refresh })}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 10,
                        border: `1px solid ${sel.auto_refresh ? T.success + '44' : T.border}`,
                        background: sel.auto_refresh ? `${T.success}15` : T.surface,
                        color: sel.auto_refresh ? T.success : T.textMuted, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      }}>
                      <FaSync style={{ fontSize: 10 }} /> {t('app.tracking.auto')} {sel.auto_refresh ? t('app.tracking.on') : t('app.tracking.off')}
                    </button>
                    <button className="trk-btn" onClick={handleRefresh} disabled={refreshing}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5, padding: '7px 16px', borderRadius: 10,
                        border: 'none', background: T.primary, color: '#fff', fontSize: 12, fontWeight: 600,
                        cursor: refreshing ? 'wait' : 'pointer', opacity: refreshing ? 0.6 : 1,
                      }}>
                      {refreshing ? <ActivityIndicator size={10} color="#fff" /> : <FaSync style={{ fontSize: 10 }} />}
                      {t('app.tracking.refreshNow')}
                    </button>
                    <button className="trk-btn" onClick={handleEnrich} disabled={enriching}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5, padding: '7px 16px', borderRadius: 10,
                        border: 'none', background: '#bf5af2', color: '#fff', fontSize: 12, fontWeight: 600,
                        cursor: enriching ? 'wait' : 'pointer', opacity: enriching ? 0.6 : 1,
                      }}>
                      {enriching ? <ActivityIndicator size={10} color="#fff" /> : <FaSearch style={{ fontSize: 10 }} />}
                      {t('app.tracking.getInsights')}
                    </button>
                    <div style={{ flex: 1 }} />
                    <div style={{ position: 'relative' }}>
                      <button className="trk-btn" onClick={(e) => { e.stopPropagation(); setActionDropdown(!actionDropdown); }}
                        style={{
                          padding: '7px 10px', borderRadius: 10, border: `1px solid ${T.border}`,
                          background: T.surface, color: T.textMuted, cursor: 'pointer', fontSize: 12,
                        }}>
                        <FaChevronDown />
                      </button>
                      {actionDropdown && (
                        <div className="trk-dropdown" onClick={e => e.stopPropagation()}>
                          {sel.status === 'active' && (
                            <div className="trk-dropdown-item" onClick={() => handleUpdate({ status: 'paused' })} style={{ color: '#ffd60a' }}>
                              <FaPause /> {t('app.tracking.pauseTracking')}
                            </div>
                          )}
                          {sel.status === 'paused' && (
                            <div className="trk-dropdown-item" onClick={() => handleUpdate({ status: 'active' })} style={{ color: '#30d158' }}>
                              <FaPlay /> {t('app.tracking.resumeTracking')}
                            </div>
                          )}
                          {(sel.status === 'active' || sel.status === 'paused' || sel.status === 'completed') && (
                            <div className="trk-dropdown-item" onClick={() => handleUpdate({ status: 'archived' })} style={{ color: '#8e8e93' }}>
                              <FaArchive /> {t('app.tracking.archive')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Change 2: Hero Baseline Comparison ── */}
                {baseline && latest && latest.delta?.since_baseline && (() => {
                  const sb = latest.delta.since_baseline;
                  const topChanges = Object.entries(sb)
                    .filter(([k, v]) => typeof v === 'object' && v.change != null && v.pct_change != null && Math.abs(v.change) > 0)
                    .sort((a, b) => Math.abs(b[1].change) - Math.abs(a[1].change))
                    .slice(0, 4);

                  if (topChanges.length === 0) return null;

                  return (
                    <div className="trk-section">
                      <div className="trk-section-hdr">
                        <div style={{ width: 40, height: 3, borderRadius: 2, background: T.success, position: 'absolute', bottom: -1, insetInlineStart: 0 }} />
                        <FaChartLine style={{ fontSize: 12, color: T.success }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: T.textSec, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                          {t('app.tracking.sinceBaseline')}
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                        {topChanges.map(([key, val]) => {
                          const metricName = t(`app.tracking.metricKeys.${key}`, { defaultValue: key.replace(/_/g, ' ') });
                          const magnitude = Math.abs(val.pct_change || 0);
                          const maxBar = 100;
                          const barWidth = Math.min(magnitude, maxBar);
                          const color = val.change >= 0 ? T.success : T.error;

                          return (
                            <div key={key} style={{
                              padding: 16, borderRadius: 12, background: T.surface,
                              border: `1px solid ${T.border}`, position: 'relative', overflow: 'hidden',
                            }}>
                              <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>
                                {metricName}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
                                <span style={{ fontSize: 13, color: T.textSec }}>{fmtNum(val.previous)}</span>
                                <FaArrowUp style={{ fontSize: 10, color: color, transform: val.change < 0 ? 'rotate(180deg)' : 'none' }} />
                                <span style={{ fontSize: 20, fontWeight: 700, color: T.text }}>{fmtNum(val.current)}</span>
                              </div>
                              <div style={{ fontSize: 14, fontWeight: 700, color: color, marginBottom: 6 }}>
                                {val.pct_change >= 0 ? '+' : ''}{val.pct_change.toFixed(0)}%
                              </div>
                              <div style={{
                                height: 4, borderRadius: 2, background: `${T.border}44`, overflow: 'hidden',
                              }}>
                                <div style={{
                                  width: `${barWidth}%`, height: '100%', background: color,
                                  transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                                }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* ── Change 5: Hidden Technical Metadata ── */}
                <details style={{ marginBottom: 20 }}>
                  <summary style={{
                    cursor: 'pointer', fontSize: 12, fontWeight: 600, color: T.textMuted,
                    padding: '10px 14px', background: T.card, border: `1px solid ${T.border}`,
                    borderRadius: 10, userSelect: 'none',
                  }}>
                    {t('app.tracking.technicalDetails')}
                  </summary>
                  <div style={{ padding: '12px 14px', background: T.card, border: `1px solid ${T.border}`, borderTop: 'none', borderRadius: '0 0 10px 10px', marginTop: -10 }}>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11 }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <FaSync style={{ fontSize: 9, color: T.primary }} />
                        <span style={{ color: T.textMuted }}>{t('app.tracking.schedule')}:</span>
                        <span style={{ color: T.text, fontWeight: 600 }}>{sel.schedule_pattern}</span>
                      </div>
                      {sel.next_check_at && sel.status === 'active' && (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <FaCog style={{ fontSize: 9, color: T.success }} />
                          <span style={{ color: T.textMuted }}>{t('app.tracking.nextCheck')}:</span>
                          <span style={{ color: T.text, fontWeight: 600 }}>{_timeAgo(sel.next_check_at, t)}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <FaEye style={{ fontSize: 9, color: T.textMuted }} />
                        <span style={{ color: T.textMuted }}>{t('app.tracking.snapshots')}:</span>
                        <span style={{ color: T.text, fontWeight: 600 }}>{sel.snapshot_index}</span>
                      </div>
                      {sel.action_queue_id && (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <FaBolt style={{ fontSize: 9, color: T.warning }} />
                          <span style={{ color: T.warning, fontWeight: 600 }}>{t('app.tracking.fromActionQueue')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </details>

                {/* Show last checked prominently outside technical details */}
                {sel.last_checked_at && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', marginBottom: 20,
                    background: T.card, border: `1px solid ${T.border}`, borderRadius: 10,
                  }}>
                    <FaEye style={{ fontSize: 11, color: T.textMuted }} />
                    <span style={{ fontSize: 12, color: T.textMuted }}>{t('app.tracking.lastChecked')}:</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{_timeAgo(sel.last_checked_at, t)}</span>
                  </div>
                )}

                {/* ── Change 3: Merged Timeline + Events ── */}
                <div className="trk-section">
                  <div className="trk-section-hdr">
                    <div style={{ width: 40, height: 3, borderRadius: 2, background: T.success, position: 'absolute', bottom: -1, insetInlineStart: 0 }} />
                    <FaEye style={{ fontSize: 12, color: T.success }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.textSec, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                      {t('app.tracking.timeline')}
                    </span>
                    <span style={{ fontSize: 10, color: T.textMuted, marginLeft: 'auto' }}>
                      {snaps.length} snapshots, {events.length} events
                    </span>
                  </div>
                  {snaps.length === 0 && events.length === 0 ? (
                    <div style={{ color: T.textMuted, fontSize: 13, textAlign: 'center', padding: 24, background: T.surface, borderRadius: 10, border: `2px dashed ${T.border}` }}>
                      {t('app.tracking.noSnapshots')}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {(() => {
                        const mergedTimeline = [];

                        snaps.forEach(snap => {
                          mergedTimeline.push({ type: 'snapshot', data: snap, time: new Date(snap.captured_at).getTime() });
                        });

                        events.forEach(evt => {
                          mergedTimeline.push({ type: 'event', data: evt, time: new Date(evt.detected_at).getTime() });
                        });

                        mergedTimeline.sort((a, b) => b.time - a.time);

                        return mergedTimeline.map((item, i) => {
                          const isLast = i === mergedTimeline.length - 1;
                          const isFirst = i === 0;

                          if (item.type === 'snapshot') {
                            const snap = item.data;
                            const isSignificant = snap.significance >= 0.4;
                            const offsetLabel = snap.is_baseline ? t('app.tracking.baseline') : `#${snap.snapshot_index}`;

                            const KEY_METRICS = ['engagement_total', 'likes', 'views', 'shares'];
                            const keyMetrics = snap.metrics
                              ? Object.entries(snap.metrics)
                                  .filter(([k, v]) => !['content_hash', 'content_exists', 'enrichment', 'narrative_status', 'is_trending'].includes(k) && typeof v === 'number')
                                  .sort(([a], [b]) => {
                                    const ai = KEY_METRICS.indexOf(a);
                                    const bi = KEY_METRICS.indexOf(b);
                                    if (ai !== -1 && bi !== -1) return ai - bi;
                                    if (ai !== -1) return -1;
                                    if (bi !== -1) return 1;
                                    return 0;
                                  })
                                  .slice(0, 4)
                              : [];

                            const sc = isSignificant ? sigColor(snap.significance) : T.border;
                            const timeStr = snap.captured_at ? _timeAgo(snap.captured_at, t) : '';
                            const dateStr = snap.captured_at ? new Date(snap.captured_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

                            return (
                              <div key={`snap-${snap.id}`} style={{ display: 'flex', gap: 0, minHeight: isLast ? undefined : 60 }}>
                                <div style={{ width: 64, flexShrink: 0, textAlign: 'right', paddingRight: 12, paddingTop: 2 }}>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: isFirst ? T.primary : T.textSec, lineHeight: 1.2 }}>{timeStr}</div>
                                  <div style={{ fontSize: 9, color: T.textMuted, marginTop: 1 }}>{dateStr}</div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
                                  {isFirst ? (
                                    <FaArrowUp style={{ fontSize: 16, color: T.primary, flexShrink: 0 }} />
                                  ) : (
                                    <div style={{
                                      width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                                      background: isSignificant ? sc : T.border,
                                      boxShadow: isSignificant ? `0 0 8px ${sc}44` : 'none',
                                      marginTop: 2,
                                    }} />
                                  )}
                                  {!isLast && (
                                    <div style={{ width: 2, flex: 1, minHeight: 20, background: T.border, opacity: 0.5 }} />
                                  )}
                                </div>

                                <div style={{
                                  flex: 1, minWidth: 0, marginLeft: 12, marginBottom: isLast ? 0 : 12,
                                  background: T.card, border: `1px solid ${isSignificant ? sc + '40' : T.border}`,
                                  borderRadius: 12, padding: '14px 16px',
                                  boxShadow: isSignificant ? `0 2px 12px ${sc}15` : '0 1px 4px rgba(0,0,0,0.04)',
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: keyMetrics.length > 0 ? 12 : 0 }}>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: isFirst ? T.primary : T.text }}>{offsetLabel}</span>
                                    {snap.significance != null && snap.significance > 0 && (
                                      <span style={{
                                        fontSize: 10, fontWeight: 700, color: sigColor(snap.significance), marginLeft: 'auto',
                                        padding: '2px 8px', borderRadius: 6, background: `${sigColor(snap.significance)}12`,
                                      }}>
                                        {snap.significance.toFixed(2)}
                                      </span>
                                    )}
                                  </div>

                                  {keyMetrics.length > 0 && (
                                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(keyMetrics.length, 4)}, 1fr)`, gap: 6, marginBottom: snap.content_exists === false || snap.metrics?.narrative_status ? 10 : 0 }}>
                                      {keyMetrics.map(([key, val]) => {
                                        const shortLabel = t(`app.tracking.metricKeys.${key}`, { defaultValue: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) });
                                        const delta = snap.delta && snap.delta[key];
                                        const pctChange = delta && typeof delta === 'object' ? delta.pct_change : (snap.delta?.[key + '_pct'] ?? null);
                                        return (
                                          <div key={key} style={{
                                            padding: '8px 6px', borderRadius: 8, background: T.surface,
                                            border: `1px solid ${T.border}`, textAlign: 'center',
                                          }}>
                                            <div style={{ fontSize: 8, color: T.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>
                                              {shortLabel}
                                            </div>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: T.text, lineHeight: 1 }}>{fmtNum(val)}</div>
                                            {pctChange != null && pctChange !== 0 && (
                                              <div style={{
                                                fontSize: 10, fontWeight: 700, marginTop: 3,
                                                color: pctChange >= 0 ? '#30d158' : '#ff453a',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2,
                                              }}>
                                                {pctChange >= 0 ? <FaArrowUp style={{ fontSize: 7 }} /> : <FaArrowDown style={{ fontSize: 7 }} />}
                                                {fmtDeltaPct(pctChange)}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {(snap.content_exists === false || snap.metrics?.narrative_status || snap.metrics?.is_trending) && (
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                      {snap.content_exists === false && (
                                        <span style={{ fontSize: 10, fontWeight: 600, color: '#ff453a', padding: '2px 8px', borderRadius: 6, background: 'rgba(255,69,58,0.12)' }}>{t('app.tracking.contentRemoved')}</span>
                                      )}
                                      {snap.metrics?.narrative_status && (
                                        <span style={{ fontSize: 10, fontWeight: 500, color: T.textSec, padding: '2px 8px', borderRadius: 6, background: T.surface, border: `1px solid ${T.border}` }}>{snap.metrics.narrative_status}</span>
                                      )}
                                      {snap.metrics?.is_trending === true && (
                                        <span style={{ fontSize: 10, fontWeight: 600, color: '#ff9f0a', padding: '2px 8px', borderRadius: 6, background: 'rgba(255,159,10,0.12)' }}>{t('app.tracking.trending')}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          } else {
                            const evt = item.data;
                            const EIcon = EVENT_ICONS[evt.event_type] || FaBolt;
                            const eColor = EVENT_COLORS[evt.event_type] || T.primary;
                            const timeStr = evt.detected_at ? _timeAgo(evt.detected_at, t) : '';
                            const dateStr = evt.detected_at ? new Date(evt.detected_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

                            return (
                              <div key={`evt-${evt.id}`} style={{ display: 'flex', gap: 0, minHeight: isLast ? undefined : 60 }}>
                                <div style={{ width: 64, flexShrink: 0, textAlign: 'right', paddingRight: 12, paddingTop: 2 }}>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: T.textSec, lineHeight: 1.2 }}>{timeStr}</div>
                                  <div style={{ fontSize: 9, color: T.textMuted, marginTop: 1 }}>{dateStr}</div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
                                  <FaBolt style={{ fontSize: 12, color: eColor, flexShrink: 0, marginTop: 2 }} />
                                  {!isLast && (
                                    <div style={{ width: 2, flex: 1, minHeight: 20, background: T.border, opacity: 0.5 }} />
                                  )}
                                </div>

                                <div style={{
                                  flex: 1, minWidth: 0, marginLeft: 12, marginBottom: isLast ? 0 : 12,
                                  background: T.card, border: `1px solid ${T.border}`,
                                  borderLeft: `4px solid ${eColor}`,
                                  borderRadius: 12, padding: '12px 16px',
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                    <div style={{
                                      width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      background: `${eColor}15`, flexShrink: 0,
                                    }}>
                                      <EIcon style={{ fontSize: 12, color: eColor }} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4, lineHeight: 1.4 }}>{evt.description}</div>
                                      <div style={{ display: 'flex', gap: 10, fontSize: 11, color: T.textMuted, alignItems: 'center' }}>
                                        <span style={{
                                          color: sigColor(evt.significance || 0), fontWeight: 700,
                                          padding: '1px 6px', borderRadius: 6, background: `${sigColor(evt.significance || 0)}12`,
                                          fontSize: 10,
                                        }}>
                                          {(evt.significance ?? 0).toFixed(2)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        });
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Draggable divider (right) ── */}
            <div className="trk-divider" onMouseDown={(e) => startDrag('right', e)} />

            {/* ── RIGHT column: insights & analytics (Change 6: reordered hierarchy) ── */}
            <div style={{ width: rightW, flexShrink: 0, overflowY: 'auto', borderLeft: `1px solid ${T.border}`, background: T.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: `1px solid ${T.border}`, color: T.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0 }}>
                {sel.narrative_id ? t('app.tracking.narrativeContext')
                  : sel.source_platform ? t('app.tracking.sourceContent')
                  : t('app.tracking.insights')}
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px' }}>

                {/* ── 1. AI Insights (FIRST, most prominent) ── */}
                {latest && latest.metrics && latest.metrics.enrichment ? (
                  <div style={{
                    background: `linear-gradient(135deg, ${T.elevated || T.card}, ${T.card})`,
                    border: `1px solid #bf5af222`, borderRadius: 14, padding: 20, marginBottom: 20,
                    borderLeft: `4px solid #bf5af2`,
                    boxShadow: '0 2px 12px rgba(191,90,242,0.06)',
                  }}>
                    <div className="trk-section-hdr" style={{ borderBottomColor: '#bf5af222' }}>
                      <div style={{ width: 40, height: 3, borderRadius: 2, background: '#bf5af2', position: 'absolute', bottom: -1, insetInlineStart: 0 }} />
                      <FaSearch style={{ fontSize: 11, color: '#bf5af2' }} />
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#bf5af2', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        {t('app.tracking.aiInsights')}
                      </span>
                      {latest.metrics.enrichment.has_new_developments && (
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 9, fontWeight: 700, color: '#ff9f0a', background: 'rgba(255,159,10,0.12)', marginLeft: 'auto' }}>
                          {t('app.tracking.newDevelopments')}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6, marginBottom: 12 }}>
                      {latest.metrics.enrichment.summary}
                    </div>
                    {latest.metrics.enrichment.search_results && latest.metrics.enrichment.search_results.length > 0 && (
                      <div style={{ borderTop: `1px solid ${T.border}33`, paddingTop: 10 }}>
                        <div style={{ fontSize: 9, color: T.textMuted, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('app.tracking.sources')}</div>
                        {latest.metrics.enrichment.search_results.slice(0, 4).map((sr, i) => (
                          <div key={i} style={{ marginBottom: 6 }}>
                            {sr.href ? (
                              <a href={sr.href} target="_blank" rel="noopener noreferrer"
                                style={{ fontSize: 11, color: T.primary, textDecoration: 'none', fontWeight: 500 }}>
                                {sr.title || sr.href}
                              </a>
                            ) : (
                              <span style={{ fontSize: 11, color: T.text, fontWeight: 500 }}>{sr.title}</span>
                            )}
                            {sr.body && <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2, lineHeight: 1.3 }}>{sr.body.slice(0, 100)}{sr.body.length > 100 ? '...' : ''}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : enriching ? (
                  <div style={{
                    background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
                    padding: 20, marginBottom: 20, textAlign: 'center',
                  }}>
                    <ActivityIndicator size="small" color="#bf5af2" />
                    <div style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>{t('app.tracking.loadingInsights')}</div>
                  </div>
                ) : null}

                {/* ── 2. Engagement Ratios (Change 4) ── */}
                {latest?.delta?.ratios && (() => {
                  const ratios = latest.delta.ratios;
                  const hasRatios = ratios.engagement_rate != null || ratios.reply_ratio != null || ratios.share_ratio != null;
                  if (!hasRatios) return null;

                  return (
                    <div style={{
                      background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
                      padding: 18, marginBottom: 20,
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FaChartLine style={{ fontSize: 11, color: T.primary }} />
                        {t('app.tracking.viralityIndicators')}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {ratios.engagement_rate != null && (
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <span style={{ fontSize: 11, color: T.textSec, fontWeight: 500 }}>{t('app.tracking.engagementRate')}</span>
                              <span style={{ fontSize: 13, fontWeight: 700, color: ratios.engagement_rate > 3 ? T.success : T.text }}>
                                {ratios.engagement_rate.toFixed(1)}%
                              </span>
                            </div>
                            <div style={{ height: 6, borderRadius: 3, background: `${T.border}44`, overflow: 'hidden' }}>
                              <div style={{
                                width: `${Math.min(ratios.engagement_rate * 2, 100)}%`,
                                height: '100%', borderRadius: 3,
                                background: ratios.engagement_rate > 3 ? T.success : T.textMuted,
                                transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                              }} />
                            </div>
                            <div style={{ fontSize: 9, color: T.textMuted, marginTop: 3 }}>{t('app.tracking.viewersEngaged')}</div>
                          </div>
                        )}
                        {ratios.reply_ratio != null && (
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <span style={{ fontSize: 11, color: T.textSec, fontWeight: 500 }}>{t('app.tracking.replyRatio')}</span>
                              <span style={{ fontSize: 13, fontWeight: 700, color: ratios.reply_ratio > 15 ? T.warning : T.text }}>
                                {ratios.reply_ratio.toFixed(1)}%
                              </span>
                            </div>
                            <div style={{ height: 6, borderRadius: 3, background: `${T.border}44`, overflow: 'hidden' }}>
                              <div style={{
                                width: `${Math.min(ratios.reply_ratio * 2, 100)}%`,
                                height: '100%', borderRadius: 3,
                                background: ratios.reply_ratio > 15 ? T.warning : T.textMuted,
                                transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                              }} />
                            </div>
                            <div style={{ fontSize: 9, color: T.textMuted, marginTop: 3 }}>
                              {ratios.reply_ratio > 15 ? t('app.tracking.ratioLevelControversial') : t('app.tracking.ratioLevelNormal')} {t('app.tracking.replyRate')}
                            </div>
                          </div>
                        )}
                        {ratios.share_ratio != null && (
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <span style={{ fontSize: 11, color: T.textSec, fontWeight: 500 }}>{t('app.tracking.shareRatio')}</span>
                              <span style={{ fontSize: 13, fontWeight: 700, color: ratios.share_ratio > 5 ? T.success : T.text }}>
                                {ratios.share_ratio.toFixed(1)}%
                              </span>
                            </div>
                            <div style={{ height: 6, borderRadius: 3, background: `${T.border}44`, overflow: 'hidden' }}>
                              <div style={{
                                width: `${Math.min(ratios.share_ratio * 4, 100)}%`,
                                height: '100%', borderRadius: 3,
                                background: ratios.share_ratio > 5 ? T.success : T.textMuted,
                                transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                              }} />
                            </div>
                            <div style={{ fontSize: 9, color: T.textMuted, marginTop: 3 }}>
                              {ratios.share_ratio > 5 ? t('app.tracking.ratioLevelViral') : t('app.tracking.ratioLevelNormal')} {t('app.tracking.shareRate')}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* ── 3. Sparkline Trends ── */}
                {snaps.length >= 2 && (
                  <div style={{
                    background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
                    padding: 18, marginBottom: 20,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FaChartLine style={{ fontSize: 11, color: T.purple }} />
                      {t('app.tracking.trends')}
                    </div>
                    {snaps.some(s => s.engagement_total != null) && (
                      <MiniSparkline values={snaps.map(s => s.engagement_total || 0)} color="#0a84ff" label={t('app.tracking.engagement')} current={latest?.engagement_total} />
                    )}
                    {snaps.some(s => s.sentiment_score != null) && (
                      <MiniSparkline values={snaps.map(s => s.sentiment_score || 0)} color="#30d158" label={t('app.tracking.sentiment')} current={latest?.sentiment_score} />
                    )}
                    {snaps.some(s => s.narrative_msg_count != null) && (
                      <MiniSparkline values={snaps.map(s => s.narrative_msg_count || 0)} color="#ff9f0a" label={t('app.tracking.messages')} current={latest?.narrative_msg_count} />
                    )}
                  </div>
                )}

                {/* ── 4. Source Content embed — always visible ── */}
                {sel.source_url && sel.source_platform && (() => {
                  const isTikTok = sel.source_platform === 'tiktok';
                  const isTwitter = sel.source_platform === 'twitter';
                  const isInstagram = sel.source_platform === 'instagram';
                  const tiktokId = isTikTok ? extractTikTokId(sel.source_url) : null;
                  const tweetId = isTwitter ? extractTweetId(sel.source_url) : null;
                  const PlatIcon = PLATFORM_ICONS[sel.source_platform];
                  const platColor = PLATFORM_COLORS[sel.source_platform] || T.textMuted;

                  return (
                    <div style={{ marginBottom: 16 }}>
                      {isTikTok && tiktokId && (
                        <div style={{ borderRadius: 12, overflow: 'hidden', background: '#000', border: `1px solid ${T.border}` }}>
                          <iframe
                            src={`https://www.tiktok.com/embed/v2/${tiktokId}`}
                            style={{ width: '100%', height: 500, border: 'none' }}
                            allowFullScreen
                            allow="encrypted-media; autoplay"
                            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                          />
                          <div style={{ padding: '10px 14px', borderTop: `1px solid ${T.border}`, background: T.card }}>
                            <a href={sel.source_url} target="_blank" rel="noopener noreferrer"
                              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: platColor, fontWeight: 600, textDecoration: 'none' }}>
                              <FaExternalLinkAlt style={{ fontSize: 9 }} /> {t('app.tracking.viewOriginal')}
                            </a>
                          </div>
                        </div>
                      )}

                      {isTwitter && tweetId && (
                        <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${T.border}`, background: T.card }}>
                          <div style={{ padding: 12 }}>
                            <blockquote className="twitter-tweet" data-theme="dark" data-conversation="none">
                              <a href={sel.source_url}>Loading tweet...</a>
                            </blockquote>
                          </div>
                        </div>
                      )}

                      {isInstagram && (
                        <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${T.border}`, background: '#000' }}>
                          <iframe
                            src={`${sel.source_url.replace(/\/$/, '')}/embed`}
                            style={{ width: '100%', height: 500, border: 'none' }}
                            allowFullScreen
                            allow="encrypted-media"
                            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                          />
                          <div style={{ padding: '10px 14px', borderTop: `1px solid ${T.border}`, background: T.card }}>
                            <a href={sel.source_url} target="_blank" rel="noopener noreferrer"
                              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: PLATFORM_COLORS.instagram, fontWeight: 600, textDecoration: 'none' }}>
                              <FaExternalLinkAlt style={{ fontSize: 9 }} /> {t('app.tracking.viewOriginal')}
                            </a>
                          </div>
                        </div>
                      )}

                      {((!isTikTok && !isTwitter && !isInstagram) || (isTikTok && !tiktokId) || (isTwitter && !tweetId)) && (
                        <div style={{ borderRadius: 12, background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${platColor}`, overflow: 'hidden' }}>
                          <div style={{ padding: '14px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                              {PlatIcon && <PlatIcon style={{ fontSize: 14, color: platColor }} />}
                              <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{sel.source_platform}</span>
                            </div>
                            <a href={sel.source_url} target="_blank" rel="noopener noreferrer"
                              style={{ fontSize: 12, color: T.primary, textDecoration: 'none', wordBreak: 'break-all', lineHeight: 1.4 }}>
                              {sel.source_url}
                            </a>
                            <div style={{ marginTop: 10 }}>
                              <a href={sel.source_url} target="_blank" rel="noopener noreferrer"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: platColor, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                                <FaExternalLinkAlt style={{ fontSize: 10 }} /> {t('app.tracking.viewOriginal')}
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── 5. Narrative Context Card ── */}
                {sel.narrative_id && (
                  <div style={{ borderRadius: 12, background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.warning}`, overflow: 'hidden', marginBottom: 16 }}>
                    <div style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <FaFire style={{ fontSize: 15, color: T.warning }} />
                        <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{sel.title}</span>
                      </div>
                      {sel.description && (
                        <p style={{ margin: '0 0 12px', color: T.textSec, fontSize: 13, lineHeight: 1.6 }}>{sel.description}</p>
                      )}
                      {latest && (
                        <div style={{ display: 'flex', gap: 16, paddingTop: 12, borderTop: `1px solid ${T.border}`, flexWrap: 'wrap' }}>
                          {latest.engagement_total != null && (
                            <div>
                              <div style={{ fontSize: 9, color: T.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('app.tracking.engagement')}</div>
                              <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{fmtNum(latest.engagement_total)}</div>
                            </div>
                          )}
                          {latest.narrative_msg_count != null && (
                            <div>
                              <div style={{ fontSize: 9, color: T.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('app.tracking.messages')}</div>
                              <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{fmtNum(latest.narrative_msg_count)}</div>
                            </div>
                          )}
                          {latest.sentiment_score != null && (
                            <div>
                              <div style={{ fontSize: 9, color: T.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('app.tracking.sentiment')}</div>
                              <div style={{ fontSize: 18, fontWeight: 700, color: latest.sentiment_score >= 0.3 ? '#30d158' : latest.sentiment_score <= -0.3 ? '#ff453a' : '#8e8e93' }}>
                                {latest.sentiment_score >= 0 ? '+' : ''}{(latest.sentiment_score * 100).toFixed(0)}%
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      <div style={{ marginTop: 12 }}>
                        <span onClick={() => navigate(`/narratives?id=${sel.narrative_id}`)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: T.primary, fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'none' }}>
                          <FaExternalLinkAlt style={{ fontSize: 9 }} /> {t('app.tracking.viewNarrative')}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
